import { $logger } from '@/boot/logger'
import { $rollbar } from '@/boot/rollbar'
import { $pegApi } from '@/boot/peg-api'
import { cloneDeep } from 'lodash-es'
import { RETURN_ITEM_PROCESS_DATA } from '@/config/returnItemProcessData'
import { useCustomerReturnValidation } from '@/composables/useCustomerReturnValidation'
import { useCustomerReturnErrorHandler } from '@/composables/useCustomerReturnErrorHandler'
import { useCustomerReturnPackagingStateStore } from '@/stores/customerReturnPackagingState.store'
import { useCustomerReturnProductStateStore } from '@/stores/customerReturnProductState.store'
import { useCustomerReturnConversationStore } from '@/stores/customerReturnConversation.store'
import { TIPS_POOL } from '@/composables/useTipsDrawer'
import { shuffleArray } from '@/utils/array.util'

/**
 * @typedef {Object} ProcessingItem
 * @property {string} id - Session item ID
 * @property {string} lineItemSessionId - Backend session item ID
 * @property {string} platformOrderLineItemId - Platform order line item ID
 * @property {Array} images - Uploaded images for this item
 * @property {Array} returnReasons - Reasons for return
 * @property {number} returnCount - Which return count this is for the line item
 * @property {Array} routingHistory - History of routing decisions
 * @property {Object} shipping - Shipping information
 * @property {boolean} shipping.delivered - Whether item was delivered
 * @property {boolean} shipping.inTransit - Whether item is in transit
 * @property {boolean} shipping.stuck - Whether item is stuck in transit
 */

/**
 * Customer Return Session Store
 *
 * Manages the return session lifecycle and item processing queue.
 * This store handles:
 * - Return session creation and management
 * - Item processing queue building from selected items
 * - Current item tracking and navigation
 * - Item session state management
 *
 * @public API:
 * - State: returnSessionId, itemsToProcess, processingItemIndex, loading, orderLookupToken
 * - Computed: currentItem, nextItem, itemIsDelivered, itemIsInTransit, itemIsStuck, hasActiveSession, hasItemsToProcess, totalItemCount
 * - Actions: startSession, initializeReturnProcess, setProcessingItemIndex, moveToNextItem, getCurrentItem, updateItemToProcess, reset, setOrderLookupToken
 *
 * @store customerReturnSession
 */

// ============================================================================
// PRIVATE HELPER FUNCTIONS
// ============================================================================

/**
 * Validate item object structure
 * @param {any} item - Item to validate
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {boolean} True if valid
 * @throws {Error} If validation fails
 * @private
 */
function _validateItemObject(item, logger) {
  if (!item || typeof item !== 'object') {
    logger.error('Invalid item object', { item })
    throw new Error('Item must be a valid object')
  }
  return true
}

/**
 * Validate processing index is within bounds
 * @param {number} index - Index to validate
 * @param {Array} items - Items array
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {boolean} True if valid
 * @throws {Error} If validation fails
 * @private
 */
function _validateProcessingIndex(index, items, logger) {
  const { isValidItemIndex } = useCustomerReturnValidation()

  if (!isValidItemIndex(index, items)) {
    logger.error('Invalid index', { index, type: typeof index, maxIndex: items.length - 1 })
    throw new Error('Index must be a valid non-negative number within bounds')
  }
  return true
}

/**
 * Build processing queue items from selected order items
 * Creates individual items for each quantity
 * @param {Array} selectedItems - Selected order items
 * @param {string} retailerId - Retailer ID
 * @param {string} orderNumber - Order number
 * @param {string} sessionId - Return session ID
 * @param {Function} startItemSession - Function to start item session
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {Promise<ProcessingItem[]>} Array of processing items
 * @private
 */
async function _buildItemQueue(selectedItems, retailerId, orderNumber, sessionId, startItemSession, logger) {
  const queue = []

  for (const item of selectedItems) {
    if (!item.selected || item.selectedQuantity === 0) {
      continue
    }

    for (let i = 1; i <= item.selectedQuantity; i++) {
      const returnSessionItem = await startItemSession(retailerId, orderNumber, sessionId, item.platformOrderLineItemId)

      if (!returnSessionItem) {
        logger.error('Failed to start item session - API returned null', {
          itemId: item.platformOrderLineItemId,
          returnCount: i,
          selectedQuantity: item.selectedQuantity,
          retailerId,
          orderNumber,
          sessionId,
        })
        throw new Error(
          `Failed to start session for item ${item.platformOrderLineItemId} (return ${i} of ${item.selectedQuantity}). Check logs for API error details.`
        )
      }

      // Add to processing queue
      queue.push({
        ...cloneDeep(item),
        ...cloneDeep(RETURN_ITEM_PROCESS_DATA),
        id: returnSessionItem.id,
        lineItemSessionId: returnSessionItem.id,
        returnCount: i,
        routingHistory: [],
      })
    }
  }

  return queue
}

// ============================================================================
// STORE DEFINITION
// ============================================================================

export const useCustomerReturnSessionStore = defineStore('customerReturnSession', () => {
  // Composables
  const { isValidItemIndex } = useCustomerReturnValidation()
  const { handleSessionStartError, handleSessionItemStartError } = useCustomerReturnErrorHandler()

  // Per-item state stores
  const packagingStateStore = useCustomerReturnPackagingStateStore()
  const productStateStore = useCustomerReturnProductStateStore()
  const conversationStore = useCustomerReturnConversationStore()

  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Token for order lookup
   * @type {Ref<string|null>}
   * @public
   */
  const orderLookupToken = ref(null)

  /**
   * Backend return session ID shared between FE and backend
   * @type {Ref<string|null>}
   * @public
   */
  const returnSessionId = ref(null)

  /**
   * Queue of items to be processed through the return flow
   * @type {Ref<ProcessingItem[]>}
   * @public
   */
  const itemsToProcess = ref([])

  /**
   * Current index in itemsToProcess array being processed
   * @type {Ref<number|null>}
   * @public
   */
  const processingItemIndex = ref(null)

  /**
   * Randomized tip order for this session
   * @type {Ref<string[]>}
   * @public
   */
  const randomizedTipOrder = ref([])

  /**
   * Current index in the randomized tip order
   * @type {Ref<number>}
   * @public
   */
  const currentTipIndex = ref(0)

  /**
   * Loading state for session operations
   * @type {Ref<Object>}
   * @public
   */
  const loading = ref({
    sessionSetup: false,
    initialization: false,
  })

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Current item being processed with null safety
   * @returns {ProcessingItem|null}
   * @public
   */
  const currentItem = computed(() => {
    if (processingItemIndex.value === null || processingItemIndex.value === undefined) {
      return null
    }
    const item = itemsToProcess.value[processingItemIndex.value]
    if (!item) {
      $logger.warn('Customer return session: Current item not found', {
        index: processingItemIndex.value,
        totalItems: itemsToProcess.value.length,
      })
      return null
    }
    return item
  })

  /**
   * Next item in the processing queue
   * @returns {ProcessingItem|null}
   * @public
   */
  const nextItem = computed(() => {
    if (processingItemIndex.value === null) return null
    const nextIndex = processingItemIndex.value + 1
    return nextIndex < itemsToProcess.value.length ? itemsToProcess.value[nextIndex] : null
  })

  /**
   * Whether current item was delivered
   * @returns {boolean}
   * @public
   */
  const itemIsDelivered = computed(() => currentItem.value?.shipping?.delivered === true)

  /**
   * Whether current item is in transit
   * @returns {boolean}
   * @public
   */
  const itemIsInTransit = computed(() => currentItem.value?.shipping?.inTransit === true)

  /**
   * Whether current item is stuck in shipping
   * @returns {boolean}
   * @public
   */
  const itemIsStuck = computed(() => currentItem.value?.shipping?.stuck === true)

  /**
   * Whether session is currently active
   * @returns {boolean}
   * @public
   */
  const hasActiveSession = computed(() => returnSessionId.value !== null)

  /**
   * Whether there are items in the processing queue
   * @returns {boolean}
   * @public
   */
  const hasItemsToProcess = computed(() => itemsToProcess.value.length > 0)

  /**
   * Total number of items in processing queue
   * @returns {number}
   * @public
   */
  const totalItemCount = computed(() => itemsToProcess.value.length)

  // ============================================================================
  // ACTIONS (PUBLIC API)
  // ============================================================================

  /**
   * Create a new return session on the backend
   * @returns {Promise<string|null>} Session ID or null on failure
   * @public
   */
  async function startSession() {
    try {
      loading.value.sessionSetup = true
      const { returnSession, success } = await $pegApi.ReturnSessions.start()

      if (!success) {
        handleSessionStartError(new Error('Session start returned success: false'))
        return null
      }

      returnSessionId.value = returnSession.id
      return returnSession.id
    } catch (error) {
      handleSessionStartError(error)
      return null
    } finally {
      loading.value.sessionSetup = false
    }
  }

  /**
   * Create a session item on the backend for a specific line item
   * @param {string} retailerId - Retailer ID
   * @param {string} orderNumber - Order number
   * @param {string} sessionId - Return session ID
   * @param {string} lineItemId - Platform order line item ID
   * @returns {Promise<Object|null>} Session item object or null on failure
   * @private - Used internally by buildProcessingQueue
   */
  async function startItemSession(retailerId, orderNumber, sessionId, lineItemId) {
    try {
      const response = await $pegApi.ReturnSessionItems.start(retailerId, orderNumber, sessionId, lineItemId)

      const { returnSessionItem } = response

      return returnSessionItem
    } catch (error) {
      $rollbar.error('Item session start exception', {
        error,
        itemId: lineItemId,
        retailerId,
        orderNumber,
        sessionId,
        errorMessage: error?.message,
        errorStack: error?.stack,
      })
      handleSessionItemStartError(error, { itemId: lineItemId })
      return null
    }
  }

  /**
   * Build processing queue from selected order items
   * Creates individual processing items for each quantity of each selected item
   * @param {Array} selectedItems - Array of selected order line items
   * @param {string} retailerId - Retailer ID
   * @param {string} orderNumber - Order number
   * @returns {Promise<boolean>} Success status
   * @private - Used internally by initializeReturnProcess
   */
  async function buildProcessingQueue(selectedItems, retailerId, orderNumber) {
    try {
      loading.value.initialization = true
      itemsToProcess.value = []

      if (!returnSessionId.value) {
        $logger.error('Cannot build processing queue: No active session')
        return false
      }

      // Build the queue using private helper
      itemsToProcess.value = await _buildItemQueue(
        selectedItems,
        retailerId,
        orderNumber,
        returnSessionId.value,
        startItemSession,
        $logger
      )

      return true
    } catch (error) {
      $logger.error('Error building processing queue', error)
      return false
    } finally {
      loading.value.initialization = false
    }
  }

  /**
   * Initialize the complete return process
   * Creates session and builds processing queue from selected items
   * @param {Array} selectedItems - Array of selected order line items
   * @param {string} retailerId - Retailer ID
   * @param {string} orderNumber - Order number
   * @returns {Promise<boolean>} Success status
   * @public
   */
  async function initializeReturnProcess(selectedItems, retailerId, orderNumber) {
    try {
      loading.value.initialization = true

      // Initialize randomized tip order for this session
      randomizedTipOrder.value = shuffleArray(TIPS_POOL)
      currentTipIndex.value = 0

      // Start new session
      const sessionId = await startSession()
      if (!sessionId) {
        return false
      }

      // Build processing queue
      const success = await buildProcessingQueue(selectedItems, retailerId, orderNumber)
      if (!success) {
        return false
      }

      return true
    } catch (error) {
      $logger.error('Error initializing return process', error)
      return false
    } finally {
      loading.value.initialization = false
    }
  }

  /**
   * Set active item for assessments
   * Internal helper to sync assessment stores with current item
   * @param {string} lineItemSessionId - Line item session ID
   * @private
   */
  function _setActiveItemForAssessments(lineItemSessionId) {
    if (!lineItemSessionId) {
      $logger.warn('Session: Cannot set active item for assessments without lineItemSessionId')
      return
    }

    packagingStateStore.setActiveItem(lineItemSessionId)
    productStateStore.setActiveItem(lineItemSessionId)
    conversationStore.setActiveItem(lineItemSessionId)
  }

  /**
   * Set order lookup token
   * @param {string|null} token - Token from order lookup API
   * @public
   */
  function setOrderLookupToken(token) {
    orderLookupToken.value = token
  }

  /**
   * Set the current processing item index
   * @param {number} index - Index in itemsToProcess array
   * @public
   */
  function setProcessingItemIndex(index) {
    if (!isValidItemIndex(index, itemsToProcess.value)) {
      return
    }
    processingItemIndex.value = index

    // Set active item for assessments
    const item = itemsToProcess.value[index]
    if (item?.lineItemSessionId) {
      _setActiveItemForAssessments(item.lineItemSessionId)
    }
  }

  /**
   * Move to the next item in the processing queue
   * @returns {boolean} True if moved to next item, false if no more items
   * @public
   */
  function moveToNextItem() {
    if (processingItemIndex.value === null) {
      processingItemIndex.value = 0

      // Set active item for assessments
      const item = itemsToProcess.value[0]
      if (item?.lineItemSessionId) {
        _setActiveItemForAssessments(item.lineItemSessionId)
      }
      return true
    }

    const nextIndex = processingItemIndex.value + 1
    if (nextIndex >= itemsToProcess.value.length) {
      return false
    }

    processingItemIndex.value = nextIndex

    // Set active item for assessments
    const item = itemsToProcess.value[nextIndex]
    if (item?.lineItemSessionId) {
      _setActiveItemForAssessments(item.lineItemSessionId)
    }

    return true
  }

  /**
   * Get current item safely
   * @returns {ProcessingItem|null}
   * @public
   */
  function getCurrentItem() {
    return currentItem.value
  }

  /**
   * Update an existing item in the processing queue
   * @param {ProcessingItem} item - Updated item object
   * @param {number} index - Index in itemsToProcess array
   * @throws {Error} When parameters are invalid
   * @public
   */
  function updateItemToProcess(item, index) {
    _validateItemObject(item, $logger)
    _validateProcessingIndex(index, itemsToProcess.value, $logger)

    itemsToProcess.value[index] = item
  }

  /**
   * Get the next tip in the randomized sequence
   * @returns {string} Next tip message
   * @public
   */
  function getNextTip() {
    // Fallback to random if no tips initialized
    if (randomizedTipOrder.value.length === 0) {
      const randomIndex = Math.floor(Math.random() * TIPS_POOL.length)
      return TIPS_POOL[randomIndex]
    }

    // Get current tip
    const tip = randomizedTipOrder.value[currentTipIndex.value]

    // Increment index with wraparound
    currentTipIndex.value = (currentTipIndex.value + 1) % randomizedTipOrder.value.length

    return tip
  }

  /**
   * Reset session state
   * @param {Object} options - Reset options
   * @param {boolean} options.clearQueue - Whether to clear processing queue
   * @public
   */
  function reset({ clearQueue = true } = {}) {
    orderLookupToken.value = null
    returnSessionId.value = null
    processingItemIndex.value = null
    randomizedTipOrder.value = []
    currentTipIndex.value = 0
    loading.value = {
      sessionSetup: false,
      initialization: false,
    }

    if (clearQueue) {
      itemsToProcess.value = []
    }

    // Reset assessment stores
    packagingStateStore.reset()
    productStateStore.reset()
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    // State
    orderLookupToken,
    returnSessionId,
    itemsToProcess,
    processingItemIndex,
    loading,
    randomizedTipOrder,
    currentTipIndex,
    // Computed
    currentItem,
    nextItem,
    itemIsDelivered,
    itemIsInTransit,
    itemIsStuck,
    hasActiveSession,
    hasItemsToProcess,
    totalItemCount,
    // Actions
    setOrderLookupToken,
    startSession,
    initializeReturnProcess,
    setProcessingItemIndex,
    moveToNextItem,
    getCurrentItem,
    updateItemToProcess,
    getNextTip,
    reset,
  }
})
